import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { aiSummaryRequest, aiQuestionRequest, aiClearChat } from './aiActions';
import { HOURLY_LIMIT, DAILY_LIMIT } from './aiReducer';

const ONE_HOUR = 60 * 60 * 1000;
const ONE_DAY = 24 * 60 * 60 * 1000;

// Helper to get value from either Immutable Record or plain object
const getValue = (obj, key) => {
    if (!obj) return undefined;
    if (typeof obj.get === 'function') {
        return obj.get(key);
    }
    return obj[key];
};

function useAi() {
    const dispatch = useDispatch();
    const ai = useSelector(state => state.ai);

    const handleSummaryRequest = useCallback((payload) => {
        dispatch(aiSummaryRequest(payload));
    }, [dispatch]);

    const handleQuestionRequest = useCallback((payload) => {
        dispatch(aiQuestionRequest(payload));
    }, [dispatch]);

    const handleClearChat = useCallback(() => {
        dispatch(aiClearChat());
    }, [dispatch]);

    // Calculate remaining requests considering time-based resets
    const rateLimitInfo = useMemo(() => {
        const now = Date.now();
        const lastHourlyReset = getValue(ai, 'lastHourlyReset') || now;
        const lastDailyReset = getValue(ai, 'lastDailyReset') || now;

        // Check if hourly count should be reset
        const hourlyElapsed = now - lastHourlyReset >= ONE_HOUR;
        const dailyElapsed = now - lastDailyReset >= ONE_DAY;

        const effectiveHourlyCount = hourlyElapsed ? 0 : (getValue(ai, 'hourlyRequestCount') || 0);
        const effectiveDailyCount = dailyElapsed ? 0 : (getValue(ai, 'dailyRequestCount') || 0);

        const hourlyRemaining = HOURLY_LIMIT - effectiveHourlyCount;
        const dailyRemaining = DAILY_LIMIT - effectiveDailyCount;

        return {
            hourlyRemaining: Math.max(0, hourlyRemaining),
            dailyRemaining: Math.max(0, dailyRemaining),
            isHourlyLimitReached: hourlyRemaining <= 0,
            isDailyLimitReached: dailyRemaining <= 0,
            isLimitReached: hourlyRemaining <= 0 || dailyRemaining <= 0,
        };
    }, [ai]);

    // Wrap ai to provide consistent getValue method
    const aiState = useMemo(() => ({
        get: (key) => getValue(ai, key),
        raw: ai,
    }), [ai]);

    return {
        ai: aiState,
        aiSummaryRequest: handleSummaryRequest,
        aiQuestionRequest: handleQuestionRequest,
        aiClearChat: handleClearChat,
        rateLimitInfo,
    };
}

export default useAi;
